import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { RootState } from '../store/store';
import { MobileUserService } from '../api/generated/services/MobileUserService';
import { ApiError } from '../api/generated/core/ApiError';
import { helper } from '../utils/helper';
import type { ShopView } from '../api/generated/models/ShopView';
import ShopSettingsModal from './ShopSettingsModal';

const SHOP_TYPE_LOGOS: Record<string, string> = {
  jtl_shop_5: require('../assets/icons/logos/jtl.ico'),
  jtl_wawi_1_5: require('../assets/icons/logos/jtl.ico'),
  jtl_wawi_1_6: require('../assets/icons/logos/jtl.ico'),
  shopify_shop: require('../assets/icons/logos/shopify.ico'),
};

function ShopTypeIcon({ shopType }: { shopType: string }) {
  const src = SHOP_TYPE_LOGOS[shopType];
  if (!src) return null;
  return <img className="store-tab-icon" src={src} alt="" width="20" height="20" />;
}

function getShopLabel(host: string, index: number, t: (key: string, opts?: any) => string): string {
  if (!host) return t('dashboard_stats_site', { n: index + 1 });
  const h = host.trim();
  if (h.startsWith('http://') || h.startsWith('https://')) {
    try {
      return new URL(h).hostname || h;
    } catch {
      return h;
    }
  }
  return h;
}

const ShopSettingsTab: React.FC = () => {
  const { t } = useTranslation();
  const profile = useSelector((state: RootState) => state.profile);
  const { items: subscriptions } = useSelector((state: RootState) => state.licenses);
  const isSubuser = !!profile.contact?.parentUserId;

  const [shops, setShops] = useState<ShopView[]>([]);
  const [loading, setLoading] = useState(true);
  const [licenseRequired, setLicenseRequired] = useState(false);
  const [selectedShop, setSelectedShop] = useState<ShopView | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const minSyncInterval = useMemo(() => {
    const mobileSub = subscriptions.find(
      (s) => s.pluginName === 'mobile' && helper.isSubscriptionActive(s)
    );
    if (!mobileSub?.options) return 0;

    const syncOption = mobileSub.options.find((opt) => opt.slug === 'sync_interval');
    return syncOption ? parseInt(syncOption.value, 10) || 0 : 0;
  }, [subscriptions]);

  const fetchShops = useCallback(() => {
    setLoading(true);
    MobileUserService.getAppMobileUserShopList()
      .then((list) => {
        setShops(list ?? []);
        setLicenseRequired(false);
      })
      .catch((err) => {
        if (err instanceof ApiError) {
          const body = err.body;
          const parsed = typeof body === 'string'
            ? (() => { try { return JSON.parse(body); } catch { return null; } })()
            : body;
          if (parsed?.error === 'license_required') {
            setLicenseRequired(true);
            setShops([]);
            return;
          }
        }
        setShops([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchShops();
  }, [fetchShops]);

  const handleOpenSettings = (shop: ShopView) => {
    setSelectedShop(shop);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedShop(null);
  };

  const handleShopSaved = (updatedShop: ShopView) => {
    setShops((prev) =>
      prev.map((s) => (s.shopId === updatedShop.shopId ? updatedShop : s))
    );
  };

  if (loading) {
    return (
      <div className="shop-settings-loading">
        <div className="order-sidebar-spinner" />
        <p>{t('shop_settings.loading')}</p>
      </div>
    );
  }

  if (licenseRequired) {
    return (
      <div className="dashboard-empty-state">
        <p className="dashboard-empty-state-text">{t('dashboard_license_required_title')}</p>
        <p className="dashboard-empty-state-hint">{t('dashboard_license_required_description')}</p>
        <Link to="/features/mobile-app" className="dashboard-empty-state-btn">
          {t('dashboard_license_required_button')}
        </Link>
      </div>
    );
  }

  if (shops.length === 0) {
    return (
      <div className="dashboard-empty-state">
        <p className="dashboard-empty-state-text">{t('shop_settings.no_shops')}</p>
        <p className="dashboard-empty-state-hint">{t('shop_settings.no_shops_hint')}</p>
      </div>
    );
  }

  return (
    <div className="shop-settings-list">
      {shops.map((shop, index) => (
        <div key={shop.shopId} className="shop-settings-row">
          <ShopTypeIcon shopType={shop.shopType} />
          <span className="shop-settings-row__host">
            {getShopLabel(shop.host, index, t)}
          </span>
          <span className={`shop-settings-row__status ${shop.isOnline ? 'shop-settings-row__status--online' : 'shop-settings-row__status--offline'}`}>
            {shop.isOnline ? t('shop_settings.shop_online') : t('shop_settings.shop_offline')}
          </span>
          <button
            type="button"
            className="shop-settings-row__gear"
            onClick={() => handleOpenSettings(shop)}
            title={t('shop_settings.settings_icon_title')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </button>
        </div>
      ))}

      {selectedShop && (
        <ShopSettingsModal
          isOpen={modalOpen}
          onClose={handleCloseModal}
          shop={selectedShop}
          onSaved={handleShopSaved}
          isSubuser={isSubuser}
          minSyncInterval={minSyncInterval}
        />
      )}
    </div>
  );
};

export default ShopSettingsTab;
